import { Card, Row, Col, Table, Badge } from "react-bootstrap";
import {
  FaIndustry,
  FaFileAlt,
  FaEnvelope,
  FaCheckCircle,
  FaExclamationTriangle
} from "react-icons/fa";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from "recharts";

/* MOCK */
import {
  kpis,
  rfqActivity,
  readiness,
  activities,
  alerts
} from "../data/mockDashboard";
import SupplierReadinessCard from "../components/Dashboard/SupplierReadinessCard";

/* COLORS */
const COLORS = ["#16a34a", "#f59e0b", "#dc2626"];

/* ICON MAPPER */
const ICON_COLOR = "#ffd02a";

const getIcon = (icon: string) => {
  const commonProps = {
    size: 20,
    style: { color: ICON_COLOR }
  };

  switch (icon) {
    case "industry":
      return <FaIndustry {...commonProps} />;
    case "file":
      return <FaFileAlt {...commonProps} />;
    case "message":
      return <FaEnvelope {...commonProps} />;
    case "check":
      return <FaCheckCircle {...commonProps} />;
    default:
      return null;
  }
};

export default function Dashboard() {
  return (
    <div className="container-fluid py-4">

      {/* ================= KPI ================= */}
      <Row className="g-3 mb-4">
        {kpis.map((kpi, index) => (
          <Col md={3} key={index}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="d-flex align-items-center gap-3">

                <div className="p-3 bg-light rounded-3">
                  {getIcon(kpi.icon)}
                </div>

                <div>
                  <h4 className="fw-bold mb-0">{kpi.value}</h4>
                  <small className="text-muted">{kpi.label}</small>
                </div>

              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ================= CHARTS ================= */}
      <Row className="g-3 mb-4">

        {/* RFQ Activity */}
        <Col md={8}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              <div className="d-flex justify-content-between mb-3">
                <h6 className="fw-semibold mb-0">RFQ Activity</h6>
                <small className="text-muted">Last 7 days</small>
              </div>

              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={rfqActivity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="rfq"
                    stroke="#dc2626"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>

            </Card.Body>
          </Card>
        </Col>

        {/* Supplier Readiness */}
        <Col md={4}>
          <SupplierReadinessCard />
        </Col>
      </Row>

      {/* ================= INDUSTRIAL ================= */}
      <Row className="g-3 mb-4">

        {/* Capacity */}
        <Col md={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Industrial Capacity</h6>
                <small className="text-muted">By sector</small>
              </div>

              {/* DATA */}
              {[
                { name: "Plastique", value: 40, color: "#3b82f6" },
                { name: "Métal", value: 35, color: "#10b981" },
                { name: "Électronique", value: 25, color: "#f59e0b" }
              ].map((item, index) => (

                <div key={index} className="mb-3">

                  {/* LABEL */}
                  <div className="d-flex justify-content-between mb-1">
                    <span className="fw-medium">{item.name}</span>
                    <span className="fw-bold">{item.value}%</span>
                  </div>

                  {/* PROGRESS BAR */}
                  <div
                    className="progress"
                    style={{
                      height: "8px",
                      borderRadius: "10px",
                      backgroundColor: "#f1f5f9"
                    }}
                  >
                    <div
                      className="progress-bar"
                      style={{
                        width: `${item.value}%`,
                        backgroundColor: item.color,
                        borderRadius: "10px"
                      }}
                    />
                  </div>

                </div>

              ))}

              {/* FOOTER INSIGHT */}
              <div className="mt-3 pt-2 border-top">

                <small className="text-muted">
                  Strongest capacity:
                </small>

                <div className="fw-bold text-primary">
                  Plastique (40%)
                </div>

              </div>

            </Card.Body>
          </Card>
        </Col>

        {/* AI Matching */}
        <Col md={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">AI Matching</h6>
                <small className="text-muted">Smart recommendation</small>
              </div>

              {/* MAIN KPI */}
              <Row className="text-center mb-3">

                <Col>
                  <small className="text-muted">RFQ</small>
                  <div className="fw-bold fs-5">#RFQ-123</div>
                </Col>

                <Col>
                  <small className="text-muted">Suppliers</small>
                  <div className="fw-bold fs-5">12</div>
                </Col>

                <Col>
                  <small className="text-muted">Match Score</small>
                  <div className="fw-bold fs-5 text-success">82%</div>
                </Col>

              </Row>

              {/* PROGRESS SCORE */}
              <div className="mb-3">

                <div className="d-flex justify-content-between">
                  <small className="text-muted">Matching Quality</small>
                  <small className="fw-bold text-success">High</small>
                </div>

                <div className="progress mt-1" style={{ height: "8px" }}>
                  <div
                    className="progress-bar bg-success"
                    style={{ width: "82%" }}
                  />
                </div>

              </div>

              {/* INSIGHT */}
              <div className="p-2 rounded-3 bg-light mb-3">

                <small className="text-muted">
                  Best candidate:
                </small>

                <div className="fw-semibold">
                  SARL TechPlast Industrie
                </div>

                <small className="text-muted">
                  ISO 9001 • Capacity OK • Delay: 3 weeks
                </small>

              </div>

              {/* ALERT / WARNING */}
              <div className="d-flex align-items-center gap-2 text-warning mb-2">
                ⚠️ <small>2 suppliers require certification upgrade</small>
              </div>

              {/* ACTION */}
              <div className="d-flex justify-content-between mt-3">

                <button className="btn btn-sm btn-outline-dark rounded-3">
                  View RFQ
                </button>

                <button className="btn btn-sm btn-success rounded-3">
                  Compare Suppliers
                </button>

              </div>

            </Card.Body>
          </Card>
        </Col>

      </Row>

      {/* ================= TABLE + ALERTS ================= */}
      <Row className="g-3">

        {/* Activity */}
        <Col md={8}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Recent Activity</h6>
                <small className="text-muted">Live platform events</small>
              </div>

              {/* LIST */}
              <div className="d-flex flex-column gap-3">

                {activities.map((a, i) => {

                  const getColor = () => {
                    switch (a.type) {
                      case "RFQ":
                        return "primary";
                      case "Response":
                        return "success";
                      case "Alert":
                        return "warning";
                      case "Message":
                        return "dark";
                      default:
                        return "secondary";
                    }
                  };

                  return (
                    <div
                      key={i}
                      className="d-flex align-items-start justify-content-between p-2 rounded-3"
                      style={{ backgroundColor: "#f8fafc" }}
                    >

                      {/* LEFT */}
                      <div className="d-flex align-items-start gap-3">

                        {/* DOT */}
                        <div
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            marginTop: "6px",
                            backgroundColor: "var(--bs-" + getColor() + ")"
                          }}
                        />

                        {/* CONTENT */}
                        <div>

                          <div className="fw-semibold">
                            {a.name}
                          </div>

                          <div className="small text-muted">
                            {a.type}
                          </div>

                        </div>

                      </div>

                      {/* RIGHT */}
                      <div className="text-end">

                        <div className="small text-muted">
                          {a.date}
                        </div>

                        <Badge bg={getColor()} className="mt-1">
                          {a.type}
                        </Badge>

                      </div>

                    </div>
                  );
                })}

              </div>

              {/* FOOTER */}
              <div className="mt-3 text-end">
                <button className="btn btn-sm btn-outline-dark rounded-3">
                  View All Activity
                </button>
              </div>

            </Card.Body>
          </Card>
        </Col>


     {/* Alerts */}
<Col md={4}>
  <Card className="border-0 shadow-sm rounded-4 h-100">
    <Card.Body>

      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-semibold mb-0">
          ⚠ Alerts
        </h6>
        <small className="text-muted">System warnings</small>
      </div>

      {/* LIST */}
      <div className="d-flex flex-column gap-3">

        {alerts.map((a, i) => {

          const getStyle = () => {
            switch (a.type) {
              case "high":
                return {
                  bg: "#fee2e2",
                  color: "#dc2626"
                };
              case "medium":
                return {
                  bg: "#fef3c7",
                  color: "#f59e0b"
                };
              default:
                return {
                  bg: "#e2e8f0",
                  color: "#475569"
                };
            }
          };

          const style = getStyle();

          return (
            <div
              key={i}
              className="p-2 rounded-3"
              style={{
                backgroundColor: style.bg
              }}
            >

              <div className="d-flex justify-content-between align-items-start">

                {/* TEXT */}
                <div style={{ color: style.color }}>
                  <div className="fw-semibold small">
                    {a.message}
                  </div>

                  <small className="text-muted">
                    {a.date}
                  </small>
                </div>

                {/* PRIORITY */}
                <span
                  className="badge"
                  style={{
                    backgroundColor: style.color,
                    color: "#fff"
                  }}
                >
                  {a.type}
                </span>

              </div>

            </div>
          );
        })}

      </div>

      {/* FOOTER */}
      <div className="mt-3 text-end">
        <button className="btn btn-sm btn-outline-dark rounded-3">
          View All Alerts
        </button>
      </div>

    </Card.Body>
  </Card>
</Col>

      </Row>

    </div>
  );
}